import { useState } from 'react';
import { Lock, Mail, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import MessagingModal from './MessagingModal';

function LockedContentCard({ property, isSubscribed = false, currentUser }) {
  const navigate = useNavigate();
  const [messagingModal, setMessagingModal] = useState({
    isOpen: false,
    messageType: 'email'
  });

  if (isSubscribed) {
    // Full access view for subscribers
    return (
      <Card className="overflow-hidden hover:shadow-2xl transition-all cursor-pointer" data-testid={`property-${property.id}`}>
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 flex items-center justify-center space-x-2">
          <Badge className="bg-emerald-600 text-white text-xs">✅ Verified Travelodges Member Resort</Badge>
        </div>
        <div className="h-56 overflow-hidden">
          <img 
            src={`${property.image}?crop=entropy&cs=srgb&fm=jpg&q=85&w=600&h=400`}
            alt={property.name}
            className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
          />
        </div>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-3">
            <Badge className="bg-emerald-100 text-emerald-800">{property.type}</Badge>
            <div className="flex items-center space-x-1">
              <span className="text-amber-400">★</span>
              <span className="font-semibold text-sm">{property.rating}</span>
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{property.name}</h3>
          <div className="flex items-center text-gray-600 mb-3">
            <span className="text-sm">{property.location}, {property.state}</span>
          </div>
          <p className="text-gray-600 text-sm mb-4">{property.description}</p>
          <div className="pt-4 border-t space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600">Starting from</div>
                <div className="text-2xl font-bold text-emerald-600">₹{property.price}</div>
                <div className="text-xs text-gray-500">per night</div>
              </div>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                View Details
              </Button>
            </div>
            
            {/* Messaging Buttons */}
            <div className="flex space-x-2 pt-2 border-t">
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                onClick={() => setMessagingModal({ isOpen: true, messageType: 'email' })}
              >
                <Mail className="h-4 w-4 mr-2" />
                Email
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="flex-1 border-green-600 text-green-600 hover:bg-green-50"
                onClick={() => setMessagingModal({ isOpen: true, messageType: 'whatsapp' })}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        </CardContent>
        
        {/* Messaging Modal */}
        <MessagingModal
          isOpen={messagingModal.isOpen}
          onClose={() => setMessagingModal({ isOpen: false, messageType: 'email' })}
          toResort={property}
          fromResort={currentUser}
          messageType={messagingModal.messageType}
        />
      </Card>
    );
  }

  // Locked view for non-subscribers
  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all relative" data-testid={`locked-property-${property.id}`}>
      <div className="absolute top-4 right-4 z-10">
        <Badge className="bg-gray-900 text-white text-xs flex items-center space-x-1">
          <Lock className="h-3 w-3" />
          <span>Members Only</span>
        </Badge>
      </div>
      <div className="h-56 overflow-hidden relative">
        <img 
          src={`${property.image}?crop=entropy&cs=srgb&fm=jpg&q=85&w=600&h=400`}
          alt="Premium Resort"
          className="w-full h-full object-cover blur-sm"
        />
        <div className="absolute inset-0 bg-black/30"></div>
      </div>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <Badge className="bg-emerald-100 text-emerald-800">{property.type}</Badge>
        </div>
        <div className="flex items-center space-x-2 mb-2">
          <Lock className="h-5 w-5 text-gray-400" />
          <h3 className="text-xl font-bold text-gray-900">Premium Resort (Name Hidden)</h3>
        </div>
        <div className="text-gray-600 mb-3">
          <div className="text-sm"><span className="font-medium">Location:</span> {property.location}, {property.state}</div>
          <div className="text-sm"><span className="font-medium">Category:</span> {property.type}</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-700">
            This resort is part of the Travelodges private network.
            Full resort names, photos, and direct connections are visible only to subscribed members.
          </p>
        </div>
        <Button 
          className="w-full bg-emerald-600 hover:bg-emerald-700"
          onClick={() => navigate('/pricing')}
          data-testid="unlock-access-btn"
        >
          <Lock className="h-4 w-4 mr-2" />
          Unlock Full Access
        </Button>
      </CardContent>
    </Card>
  );
}

export default LockedContentCard;